import React, { useEffect, useState } from "react";
import { TOOLS_WIDGETS } from "../../landingContent";
import SectionHeading from "../SectionHeading";

const TIMER_SECONDS = [480, 479, 478, 477, 476, 475, 474, 473];
const SOUND_PERCENT_FRAMES = [62, 66, 59, 71, 64, 68];
const RANDOMIZER_WINNERS = ["Avery", "Mia", "Noah", "Sofia"];
const POINT_TRACKER_FRAMES = [
  {
    activeTeamIndex: 0,
    teams: [
      ["Team 1", "12", "#FF6B6B"],
      ["Team 2", "9", "#6BCB77"],
    ],
  },
  {
    activeTeamIndex: 1,
    teams: [
      ["Team 1", "13", "#FF6B6B"],
      ["Team 2", "9", "#6BCB77"],
    ],
  },
  {
    activeTeamIndex: 1,
    teams: [
      ["Team 1", "13", "#FF6B6B"],
      ["Team 2", "10", "#6BCB77"],
    ],
  },
  {
    activeTeamIndex: 0,
    teams: [
      ["Team 1", "14", "#FF6B6B"],
      ["Team 2", "10", "#6BCB77"],
    ],
  },
];

const formatTimerSeconds = (seconds) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;

  return `${minutes}:${String(remainingSeconds).padStart(2, "0")}`;
};

const getFrame = (frames, tick) => frames[tick % frames.length];

const useWidgetAnimationTick = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const prefersReducedMotion = window.matchMedia?.("(prefers-reduced-motion: reduce)")?.matches;

    if (prefersReducedMotion) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setTick((currentTick) => (currentTick + 1) % 120);
    }, 1100);

    return () => window.clearInterval(intervalId);
  }, []);

  return tick;
};

const renderWidgetMock = (id, tick) => {
  switch (id) {
    case "timer": {
      const timerDisplay = formatTimerSeconds(getFrame(TIMER_SECONDS, tick));

      return (
        <div className="landing-v2-widget-mock landing-v2-widget-mock--timer">
          <div className="landing-v2-widget-mock__chrome">
            <span>Timer</span>
            <span className="landing-v2-widget-mock__chrome-icons">...</span>
          </div>
          <div className="landing-v2-widget-mock__timer-display">{timerDisplay}</div>
          <div className="landing-v2-widget-mock__timer-progress">
            <span />
          </div>
          <div className="landing-v2-widget-mock__timer-actions">
            <span>Start</span>
            <span>Reset</span>
          </div>
        </div>
      );
    }
    case "point-tracker": {
      const pointTrackerFrame = getFrame(POINT_TRACKER_FRAMES, tick);

      return (
        <div className="landing-v2-widget-mock landing-v2-widget-mock--point-tracker">
          <div className="landing-v2-widget-mock__chrome">
            <span>Point Tracker</span>
            <span className="landing-v2-widget-mock__chrome-icons">...</span>
          </div>
          <div className="landing-v2-widget-mock__point-grid">
            {pointTrackerFrame.teams.map(([teamName, points, color], teamIndex) => (
              <div
                key={teamName}
                className={`landing-v2-widget-mock__point-card${
                  pointTrackerFrame.activeTeamIndex === teamIndex ? " landing-v2-widget-mock__point-card--active" : ""
                }`}
                style={{ "--mock-team-color": color }}
              >
                <div className="landing-v2-widget-mock__point-name">{teamName}</div>
                <div className="landing-v2-widget-mock__point-score">{points}</div>
                <div className="landing-v2-widget-mock__point-actions">
                  <span>-</span>
                  <span>+</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      );
    }
    case "sound-meter": {
      const soundPercent = getFrame(SOUND_PERCENT_FRAMES, tick);

      return (
        <div className="landing-v2-widget-mock landing-v2-widget-mock--sound-meter">
          <div className="landing-v2-widget-mock__chrome">
            <span>Sound Meter</span>
            <span className="landing-v2-widget-mock__chrome-icons">...</span>
          </div>
          <div className="landing-v2-widget-mock__sound-card">
            <div className="landing-v2-widget-mock__sound-preset">Group Work</div>
            <div className="landing-v2-widget-mock__sound-waveform" aria-hidden="true">
              {[34, 58, 44, 76, 62, 92, 70, 48, 66, 40].map((height, index) => (
                <span key={`${height}-${index}`} style={{ "--mock-wave-height": `${height}%` }} />
              ))}
            </div>
            <div className="landing-v2-widget-mock__sound-percent">{soundPercent}%</div>
          </div>
        </div>
      );
    }
    case "whiteboard":
      return (
        <div className="landing-v2-widget-mock landing-v2-widget-mock--whiteboard">
          <div className="landing-v2-widget-mock__whiteboard-toolbar">
            <span>Pen</span>
            <span>Shape</span>
            <span>Text</span>
            <span>Clear board</span>
          </div>
          <svg className="landing-v2-widget-mock__whiteboard-canvas" viewBox="0 0 260 150" role="img" aria-label="Whiteboard sketch mockup">
            <path d="M35 98 C70 42, 108 42, 136 78 S198 115, 229 54" />
            <circle cx="76" cy="65" r="18" />
            <rect x="159" y="42" width="48" height="34" rx="8" />
            <line x1="45" y1="122" x2="220" y2="122" />
          </svg>
          <div className="landing-v2-widget-mock__board-dots">
            <span className="is-active" />
            <span />
            <span />
          </div>
        </div>
      );
    case "signal":
      return (
        <div className="landing-v2-widget-mock landing-v2-widget-mock--signal">
          <div className="landing-v2-widget-mock__chrome">
            <span>Signal</span>
            <span className="landing-v2-widget-mock__chrome-icons">...</span>
          </div>
          <div className="landing-v2-widget-mock__signal-wrap" aria-label="Signal lights cycling">
            <span className="landing-v2-widget-mock__signal-light landing-v2-widget-mock__signal-light--red" />
            <span className="landing-v2-widget-mock__signal-light landing-v2-widget-mock__signal-light--yellow" />
            <span className="landing-v2-widget-mock__signal-light landing-v2-widget-mock__signal-light--green is-active" />
          </div>
        </div>
      );
    case "randomizer": {
      const winnerName = getFrame(RANDOMIZER_WINNERS, tick);

      return (
        <div className="landing-v2-widget-mock landing-v2-widget-mock--randomizer">
          <div className="landing-v2-widget-mock__chrome">
            <span>Randomizer</span>
            <span className="landing-v2-widget-mock__chrome-icons">...</span>
          </div>
          <div className="landing-v2-widget-mock__wheel-wrap">
            <div className="landing-v2-widget-mock__wheel" />
            <div className="landing-v2-widget-mock__wheel-pointer" />
          </div>
          <div className="landing-v2-widget-mock__winner">{winnerName}</div>
        </div>
      );
    }
    default:
      return null;
  }
};

const ToolsWidgetsSection = () => {
  const animationTick = useWidgetAnimationTick();

  return (
    <section className="landing-v2__section landing-v2-tools" aria-labelledby="tools-widgets-heading">
      <div className="landing-v2__container">
        <div className="landing-v2__split landing-v2-tools__layout">
          <div className="landing-v2__split-copy">
            <SectionHeading
              headline={(
                <>
                  <span className="landing-v2-tools__headline-desktop">
                    All Your Classroom Tools,
                    <br />
                    Ready When You Need Them.
                  </span>
                  <span className="landing-v2-tools__headline-mobile">
                    {TOOLS_WIDGETS.headline}
                  </span>
                </>
              )}
              subheading={TOOLS_WIDGETS.subheading}
              centered={false}
              headingId="tools-widgets-heading"
            />

            <p className="landing-v2-tools__widget-line">{TOOLS_WIDGETS.widgetIntro}</p>
          </div>

          <div className="landing-v2-tools__mosaic" aria-label="Animated previews of Lesson Launchpad tools and widgets">
            {TOOLS_WIDGETS.widgets.map(({ id, title }, index) => (
              <div
                key={id}
                className={`landing-v2-tools__mosaic-item landing-v2-tools__mosaic-item--${index + 1}`}
                aria-label={`${title} animated preview`}
              >
                {renderWidgetMock(id, animationTick)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ToolsWidgetsSection;
